'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import {
  navTourProgressStorageKey,
  parseNavTourProgress,
  serializeNavTourProgress,
} from '@communio/nav-tour-progress';
import { resolveNavTourBootstrapStepIndex } from '@communio/nav-tour-route-sync';
import { useAuth } from '@/platform/auth/useAuth';
import { useCookieConsent } from '@/platform/cookies/CookieConsentProvider';
import { clearNavTourDone, readNavTourDone, slugFromHostname, writeNavTourDone } from './navTourPersistence';
import {
  marketplaceNavTourStepIndexAfterEnable,
  resolveNavTourTrack,
  usePollMarketplaceStatusDuringEnableStep,
} from './navTourOrchestration';
import { resolveNavTourSteps } from './resolveNavTourSteps';
import type { NavTourStep } from './navTourSteps';
import { useMarketplaceCommunityStatus } from './useMarketplaceCommunityStatus';
import { useRegisterNavTourRestart } from './NavTourControlContext';
import { isNavTourEnabledBuild } from './navTourBuildGate';
import { useGrowthPathTourActive } from '@/platform/hostgrowth/GrowthPathTourContext';

export type NavTourCoachState = {
  active: boolean;
  step: NavTourStep | null;
  stepIndex: number;
  stepCount: number;
  stepIds: string[];
  setStepIndex: (index: number) => void;
  goNext: () => void;
  goBack: () => void;
  finish: () => void;
};

function readProgress(track: string, slug: string) {
  if (typeof window === 'undefined') return null;
  return parseNavTourProgress(sessionStorage.getItem(navTourProgressStorageKey(track, slug)));
}

function writeProgress(track: string, slug: string, stepIndex: number) {
  if (typeof window === 'undefined') return;
  sessionStorage.setItem(
    navTourProgressStorageKey(track, slug),
    serializeNavTourProgress({ stepIndex, started: true }),
  );
}

function clearProgress(track: string, slug: string) {
  if (typeof window === 'undefined') return;
  sessionStorage.removeItem(navTourProgressStorageKey(track, slug));
}

/**
 * First-run nav tour state machine (persistence + steps + restart registration).
 * Chrome (route / Escape) lives in useNavTourChromeEffects.
 */
export function useNavTourCoach(): NavTourCoachState {
  const pathname = usePathname() || '/';
  const { user, isLoading } = useAuth();
  const registerRestart = useRegisterNavTourRestart();
  const growthTourActive = useGrowthPathTourActive();
  const fetchEnabled = isNavTourEnabledBuild() && !!user && !isLoading;
  const { status, reload } = useMarketplaceCommunityStatus(fetchEnabled);
  const { consent, isReady: cookiesReady } = useCookieConsent();
  const [stepIndex, setStepIndex] = useState(0);
  const [dismissed, setDismissed] = useState(false);
  const [started, setStarted] = useState(false);
  const tourBootstrapped = useRef(false);

  const track = useMemo(() => resolveNavTourTrack(status), [status]);

  const slug = useMemo(() => {
    if (status.slug) return status.slug;
    if (typeof window === 'undefined') return 'local';
    return slugFromHostname(window.location.hostname);
  }, [status.slug]);

  const steps = useMemo(() => {
    if (!track || !status.ready) return [];
    return resolveNavTourSteps(track, status, { includeTheme: true });
  }, [track, status]);

  const stepIds = useMemo(() => steps.map((s) => s.id), [steps]);

  const active =
    isNavTourEnabledBuild() &&
    !!user &&
    !isLoading &&
    !dismissed &&
    started &&
    steps.length > 0 &&
    cookiesReady &&
    !!consent &&
    !growthTourActive;
  const step = active ? steps[Math.min(stepIndex, steps.length - 1)] : null;

  useEffect(() => {
    if (!started || !track || !status.ready) return;
    const nextIdx = marketplaceNavTourStepIndexAfterEnable(
      track,
      status,
      steps[stepIndex]?.id,
      { includeTheme: true },
    );
    if (nextIdx !== null) {
      writeProgress(track, slug, nextIdx);
      setStepIndex(nextIdx);
    }
  }, [started, track, status, status.enabled, status.ready, steps, stepIndex, slug]);

  useEffect(() => {
    if (!isNavTourEnabledBuild() || isLoading || !user || !status.ready || !track || !cookiesReady || !consent) {
      return;
    }
    if (tourBootstrapped.current) return;
    tourBootstrapped.current = true;
    if (readNavTourDone(track, slug)) {
      setDismissed(true);
      clearProgress(track, slug);
      return;
    }
    const progress = readProgress(track, slug);
    const ids = resolveNavTourSteps(track, status, { includeTheme: true }).map((s) => s.id);
    setStarted(true);
    setStepIndex(resolveNavTourBootstrapStepIndex(pathname, ids, progress?.stepIndex));
  }, [isLoading, user, status, status.ready, track, slug, cookiesReady, consent, pathname]);

  useEffect(() => {
    if (!active || !track) return;
    writeProgress(track, slug, stepIndex);
  }, [active, track, slug, stepIndex]);

  usePollMarketplaceStatusDuringEnableStep(active, step?.id, status.enabled, reload);

  const finish = useCallback(() => {
    if (track) {
      writeNavTourDone(track, slug);
      clearProgress(track, slug);
    }
    setDismissed(true);
    setStarted(false);
  }, [track, slug]);

  const restart = useCallback(() => {
    if (!isNavTourEnabledBuild() || !track) return;
    clearNavTourDone(track, slug);
    clearProgress(track, slug);
    tourBootstrapped.current = true;
    setDismissed(false);
    setStarted(true);
    setStepIndex(0);
  }, [track, slug]);

  useEffect(() => {
    registerRestart(restart);
    return () => registerRestart(null);
  }, [registerRestart, restart]);

  const goNext = useCallback(() => {
    if (stepIndex >= steps.length - 1) {
      finish();
      return;
    }
    const next = stepIndex + 1;
    if (track) writeProgress(track, slug, next);
    setStepIndex(next);
  }, [finish, stepIndex, steps.length, track, slug]);

  const goBack = useCallback(() => {
    const next = Math.max(0, stepIndex - 1);
    if (track) writeProgress(track, slug, next);
    setStepIndex(next);
  }, [stepIndex, track, slug]);

  return {
    active,
    step,
    stepIndex,
    stepCount: steps.length,
    stepIds,
    setStepIndex,
    goNext,
    goBack,
    finish,
  };
}
